import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QueryKeys } from '../../api/queryKeys';
import {
  deleteUserSetting,
  getUserSettings,
  setGlobalSettings,
  setUserSetting,
  setUserSettings,
} from './api';
import { SetUserSetting, UserSetting } from './types';

/**
 * Query options of the current user's settings, for prefetching or reading them outside components.
 * Settings only change through the mutations below, which update the cache, so they never go stale.
 */
export const userSettingsQueryOptions = queryOptions({
  queryKey: [QueryKeys.USER_SETTINGS],
  queryFn: getUserSettings,
  staleTime: Infinity,
  meta: { errorMessageKey: 'errorFetchingUserSettings' },
});

/**
 * Fetches the current user's settings (global settings included).
 * @param select - Optional selector over the settings, so components re-render only when it changes.
 */
export const useUserSettingsQuery = <T = UserSetting>(
  select?: (settings: UserSetting) => T,
) => useQuery({ ...userSettingsQueryOptions, select });

/**
 * Mutation that merges settings into the cache before calling the API.
 * The last of overlapping saves to settle refetches the settings, which undoes any that failed.
 */
const useOptimisticSettingsMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<void>,
  toSettings: (variables: TVariables) => UserSetting,
  errorMessageKey: string,
) => {
  const queryClient = useQueryClient();
  const queryKey = [QueryKeys.USER_SETTINGS];
  return useMutation({
    mutationKey: queryKey,
    mutationFn,
    onMutate: async (variables) => {
      // An earlier save's refetch would overwrite this one
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<UserSetting>(queryKey, (settings) => ({
        ...settings,
        ...toSettings(variables),
      }));
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: queryKey }) === 1) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    meta: { errorMessageKey },
  });
};

/**
 * Saves a single user setting. An undefined value deletes the setting.
 */
export const useSetUserSettingMutation = () =>
  useOptimisticSettingsMutation(
    ({ key, value }: SetUserSetting) =>
      value ? setUserSetting(key, value) : deleteUserSetting(key),
    ({ key, value }) => ({ [key]: value }),
    'errorUpdatingUserSetting',
  );

/**
 * Saves several user settings at once.
 */
export const useSetUserSettingsMutation = () =>
  useOptimisticSettingsMutation(
    setUserSettings,
    (settings: UserSetting) => settings,
    'errorUpdatingUserSettings',
  );

/**
 * Saves several global settings at once.
 */
export const useSetGlobalSettingsMutation = () =>
  useOptimisticSettingsMutation(
    setGlobalSettings,
    (settings: UserSetting) => settings,
    'errorUpdatingGlobalSettings',
  );
